import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import './ThesisReviewersManagement.css';

const ThesisReviewersManagement = () => {
  // was { conference_id, section_id, thesis_id }
  const { festschrift_id, section_id, article_id } = useParams();
  const { getLocalizedName, getLocalizedFullName, getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  // Хлебные крошки
  const resetBreadcrumbs = useCallback(
    (search) => {
      const searchParams = new URLSearchParams(search);
      const confFromQuery = searchParams.get('conferenceName') || '';
      const confCached = (() => {
        try {
          return sessionStorage.getItem(`fest_name_${festschrift_id}`) || '';
        } catch {
          return '';
        }
      })();
      const confName = confFromQuery || confCached || '';

      const sectionName = searchParams.get('sectionName') || '';
      const thesisTitle = searchParams.get('thesisTitle') || '';

      const encConf = encodeURIComponent(confName);
      const encSection = encodeURIComponent(sectionName);
      const encThesis = encodeURIComponent(thesisTitle);

      return [
        {
          label: confName,
          url: `/management/publishing/${festschrift_id}?conferenceName=${encConf}`,
        },
        {
          label: sectionName,
          url:
            `/management/publishing/${festschrift_id}/sections/${section_id}/articles` +
            `?conferenceName=${encConf}&sectionName=${encSection}`,
        },
        {
          label: thesisTitle,
          url:
            `/management/publishing/${festschrift_id}/sections/${section_id}/articles/${article_id}` +
            `?conferenceName=${encConf}&sectionName=${encSection}&thesisTitle=${encThesis}`,
        },
      ];
    },
    [festschrift_id, section_id, article_id],
  );
  useUpdateBreadcrumbs(resetBreadcrumbs);

  // Текущие рецензенты
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Кандидаты на добавление
  const [participants, setParticipants] = useState([]);
  const [showAddReviewer, setShowAddReviewer] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Сортировка
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Модальное окно подтверждения
  const [confirmData, setConfirmData] = useState(null);

  const openConfirmModal = (message, onConfirm, onCancel) => {
    setConfirmData({ message, onConfirm, onCancel });
  };

  const closeConfirmModal = () => {
    setConfirmData(null);
  };

  // Получение ФИО рецензента
  const getReviewerFullName = useCallback(
    (reviewer_full_name) => {
      if (!reviewer_full_name) return '';
      const name = reviewer_full_name.name ? getLocalizedName(reviewer_full_name.name) : '';
      const surname = reviewer_full_name.surname
        ? getLocalizedName(reviewer_full_name.surname)
        : '';
      return `${name} ${surname}`.trim();
    },
    [getLocalizedName],
  );

  // Загрузить текущих рецензентов статьи
  const fetchReviewers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/management/publishing/${festschrift_id}/sections/${section_id}/articles/${article_id}/reviewers`,
      );
      setReviewers(response.data.rows || []);
      setError('');
    } catch (err) {
      console.error(err);
      setError(
        lang === 'ru' ? 'Ошибка при загрузке данных рецензентов' : 'Error loading reviewers data',
      );
    } finally {
      setLoading(false);
    }
  }, [festschrift_id, section_id, article_id, lang]);

  useEffect(() => {
    fetchReviewers();
  }, [fetchReviewers]);

  // Отсортированный список рецензентов
  const sortedReviewers = useMemo(() => {
    if (sortConfig.key === null) return reviewers;
    const sorted = [...reviewers].sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.key) {
        case 'reviewer':
          aValue = getReviewerFullName(a.reviewer_full_name).toLowerCase();
          bValue = getReviewerFullName(b.reviewer_full_name).toLowerCase();
          break;
        case 'email':
          aValue = a.reviewer_email.toLowerCase();
          bValue = b.reviewer_email.toLowerCase();
          break;
        case 'status':
          aValue = a.status.toLowerCase();
          bValue = b.status.toLowerCase();
          break;
        case 'details':
          aValue = (a.details || '').toLowerCase();
          bValue = (b.details || '').toLowerCase();
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [reviewers, sortConfig, getReviewerFullName]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        if (prev.direction === null) return { key, direction: 'asc' };
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  };

  // Если открыт блок добавления — загружаем кандидатов
  useEffect(() => {
    if (!showAddReviewer) return;
    const fetchParticipants = async () => {
      try {
        // endpoint остаётся тем же, но ID теперь article_id
        const response = await api.get(`/auth/candidates/${article_id}`);
        const candidates = response.data.map((item) => ({
          id: item.id,
          fullName: item.reviewer_full_name ? getLocalizedFullName(item.reviewer_full_name) : '',
          email: item.reviewer_email,
        }));
        setParticipants(candidates);
      } catch (err) {
        console.error(err);
      }
    };
    fetchParticipants();
  }, [showAddReviewer, article_id, getLocalizedFullName]);

  const filteredParticipants = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return participants.filter(
      (p) => p.fullName.toLowerCase().includes(q) || p.email.toLowerCase().includes(q),
    );
  }, [participants, searchQuery]);

  // Добавление рецензента
  const handleAddReviewer = (participant) => {
    openConfirmModal(
      lang === 'ru'
        ? `Добавить рецензента ${participant.fullName}?`
        : `Add reviewer ${participant.fullName}?`,
      async () => {
        try {
          const response = await api.post(
            `/management/publishing/${festschrift_id}/sections/${section_id}/articles/${article_id}/reviewers/${participant.id}`,
          );
          if (response.status === 200) {
            setReviewers((prev) => [
              ...prev,
              {
                reviewer_id: participant.id,
                reviewer_full_name: {
                  name: {
                    name_ru: participant.fullName,
                    name_en: participant.fullName,
                  },
                  surname: { name_ru: '', name_en: '' },
                },
                reviewer_email: participant.email,
                status: 'pending',
                details: null,
                review_present: false,
              },
            ]);

            setParticipants((prev) => prev.filter((p) => p.id !== participant.id));
          }
          alert(
            lang === 'ru'
              ? `Рецензент ${participant.fullName} добавлен.`
              : `Reviewer ${participant.fullName} added.`,
          );
        } catch (error) {
          console.error('Ошибка добавления рецензента:', error);
        } finally {
          closeConfirmModal();
        }
      },
      () => closeConfirmModal(),
    );
  };

  // Удаление рецензента (если статус pending и review_present = false)
  const handleDeleteReviewer = (reviewer) => {
    openConfirmModal(
      lang === 'ru'
        ? `Удалить рецензента ${getReviewerFullName(reviewer.reviewer_full_name)}?`
        : `Remove reviewer ${getReviewerFullName(reviewer.reviewer_full_name)}?`,
      async () => {
        try {
          const response = await api.delete(
            `/management/publishing/${festschrift_id}/sections/${section_id}/articles/${article_id}/reviewers/${reviewer.reviewer_id}`,
          );
          if (response.status === 200) {
            setReviewers((prev) => prev.filter((r) => r.reviewer_id !== reviewer.reviewer_id));
          }
        } catch (error) {
          console.error('Ошибка удаления рецензента:', error);
        } finally {
          closeConfirmModal();
        }
      },
      () => closeConfirmModal(),
    );
  };

  if (loading) {
    return (
      <div className="thesis-reviewers-container">
        <div className="loading-state">
          {lang === 'ru' ? 'Загрузка данных рецензентов...' : 'Loading reviewers data...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="thesis-reviewers-container">
        <div className="error-state">{error}</div>
      </div>
    );
  }

  const statusRepresentation = (review) => {
    const status = review.status;

    if (review.review_present) {
      switch (status) {
        case 'pending':
          return lang === 'ru' ? 'Ожидание ответа автора' : 'Awaiting author response';
        case 'rejected':
          return lang === 'ru' ? 'Работа отклонена' : 'Submission rejected';
        case 'in_progress':
          return lang === 'ru' ? 'В процессе' : 'In progress';
        default:
          return status;
      }
    }

    switch (status) {
      case 'pending':
        return lang === 'ru' ? 'Ожидание согласия рецензента' : 'Awaiting reviewer confirmation';
      case 'rejected':
        return lang === 'ru' ? 'Рецензент отказался от ревью' : 'Reviewer refused the review';
      default:
        return status;
    }
  };

  return (
    <div className="thesis-reviewers-container">
      <h1 className="page-title">
        {lang === 'ru' ? 'Управление рецензентами' : 'Reviewers management'}
      </h1>

      <div className="current-reviewers-section">
        <h2 className="current-reviewers-section-h2">
          {lang === 'ru' ? 'Текущие рецензенты' : 'Current reviewers'}
        </h2>
        {sortedReviewers.length > 0 ? (
          <div className="table-container">
            <table className="reviewers-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('reviewer')}>
                    {lang === 'ru' ? 'Рецензент' : 'Reviewer'}
                    {sortConfig.key === 'reviewer' && (
                      <span className="sort-icon">
                        {sortConfig.direction === 'asc'
                          ? ' ▲'
                          : sortConfig.direction === 'desc'
                            ? ' ▼'
                            : ''}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('email')}>
                    Email
                    {sortConfig.key === 'email' && (
                      <span className="sort-icon">
                        {sortConfig.direction === 'asc'
                          ? ' ▲'
                          : sortConfig.direction === 'desc'
                            ? ' ▼'
                            : ''}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('status')}>
                    {lang === 'ru' ? 'Статус' : 'Status'}
                    {sortConfig.key === 'status' && (
                      <span className="sort-icon">
                        {sortConfig.direction === 'asc'
                          ? ' ▲'
                          : sortConfig.direction === 'desc'
                            ? ' ▼'
                            : ''}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('details')}>
                    {lang === 'ru' ? 'Детали' : 'Details'}
                    {sortConfig.key === 'details' && (
                      <span className="sort-icon">
                        {sortConfig.direction === 'asc'
                          ? ' ▲'
                          : sortConfig.direction === 'desc'
                            ? ' ▼'
                            : ''}
                      </span>
                    )}
                  </th>
                  <th>{lang === 'ru' ? 'Действие' : 'Action'}</th>
                </tr>
              </thead>
              <tbody>
                {sortedReviewers.map((r) => (
                  <tr key={r.reviewer_id}>
                    <td>{getReviewerFullName(r.reviewer_full_name)}</td>
                    <td>{r.reviewer_email}</td>
                    <td>{statusRepresentation(r)}</td>
                    <td>{r.details || '-'}</td>
                    <td>
                      {!r.review_present && r.status === 'pending' && (
                        <button
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteReviewer(r);
                          }}
                        >
                          {lang === 'ru' ? 'Удалить' : 'Delete'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>{lang === 'ru' ? 'Нет рецензентов' : 'No reviewers'}</p>
        )}
      </div>

      <div className="add-reviewer-section">
        <button className="add-reviewer-btn" onClick={() => setShowAddReviewer((prev) => !prev)}>
          {showAddReviewer
            ? lang === 'ru'
              ? 'Скрыть список участников'
              : 'Hide participants list'
            : lang === 'ru'
              ? 'Добавить рецензентов'
              : 'Add reviewers'}
        </button>
        {showAddReviewer && (
          <div className="participants-section">
            <input
              type="text"
              placeholder={lang === 'ru' ? 'Поиск рецензентов...' : 'Search reviewers...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <div className="table-container">
              <table className="participants-table">
                <thead>
                  <tr>
                    <th>{lang === 'ru' ? 'Рецензент' : 'Reviewer'}</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParticipants.map((participant) => (
                    <tr key={participant.id} onClick={() => handleAddReviewer(participant)}>
                      <td>{participant.fullName}</td>
                      <td>{participant.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {confirmData && (
        <ConfirmationModal
          message={confirmData.message}
          onConfirm={confirmData.onConfirm}
          onCancel={confirmData.onCancel}
        />
      )}
    </div>
  );
};

const ConfirmationModal = ({ message, onConfirm, onCancel }) => {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  return (
    <div className="modal-overlay">
      <div className="modal-window">
        <p className="modal-message">{message}</p>
        <div className="modal-buttons">
          <button className="modal-confirm-btn" onClick={onConfirm}>
            {lang === 'ru' ? 'Подтвердить' : 'Confirm'}
          </button>
          <button className="modal-cancel-btn" onClick={onCancel}>
            {lang === 'ru' ? 'Отмена' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThesisReviewersManagement;
